import { __awaiter } from "tslib";
import { PLATFORM, SUB_PLATFORM } from '@lusha/source-platforms';
import { notify } from '@lusha-ui/notify';
import CompaniesDispatcher from '@reducers/companies/dispatcher';
import { editedContactAdapter } from '../../API/adapters';
import { showContactsByDataPoints } from '../../API/bulkApi';
import { refreshContactData } from '../../API/contactRefreshApi';
import { editContactData } from '../../API/editContactAPI';
import { submitContactFeedback } from '../../API/feedbackApi';
import { searchEmployeesByCompany } from '../../API/prospectingAPI';
import { saveContactToWishlist, updateWishedContact } from '../../API/wishlistApi';
import { USE_BULK_KEY } from '../../components/Pricing295UnlimitedExceededPopup';
import { BUTTON_TYPES } from '../../components/ShowContactByDataPoint/constants';
import messageList from '../../lib/messageList';
import { SAVE_CONTACT_SUB_PLATFORMS } from '../../const/app';
import { buildContactsDataPointDataForShow, sumNumberOfCreditsCharge } from '../../utils/showHelper';
import { getContactWishlistData } from '../../views/Wishlist/Utils';
import AccountDispatcher from '../account/dispatcher';
import { actions as appActions } from '../app/reducer';
import { actions as contactActions } from '../contact/reducer';
import { collectSavedDatapointIds, getExistingContactMapKey, mapApiWishlistContactToWishlistData, mapContactEditableData, mapEmployeesSearchFilters, mapEmployeeToContact, mapIntegrationsData, mapSearchContactToContact, mapSearchEmployeesResultToContacts, mapWishlistAddedDataPoints, } from './mappers';
import { actions } from './reducer';
import { selectEmails, selectIsWishlistContact, selectPhones } from './selectors';
const resolveExistingKey = (contacts, contact) => getExistingContactMapKey(contacts, contact);
/**
 * Builds the `metadata` payload sent with every edit-contacts request.
 * Lives here so callers only pass what they know (`itemType`, `isRevert`),
 * while the session and platform are read from the store.
 */
const buildContactEditMetadata = (state, { itemType, isRevert = false } = {}) => ({
    sessionId: state.app.sessionId,
    isRevert,
    itemType,
    platform: PLATFORM.PLUGIN,
    subPlatform: state.settings.platform,
});
const areStringArraysEqual = (left, right) => {
    if (left === right) {
        return true;
    }
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
        return false;
    }
    return left.every((value, index) => value === right[index]);
};
const getParsedContactData = (parserResult, contact) => {
    var _a, _b, _c, _d;
    const ordinalId = contact === null || contact === void 0 ? void 0 : contact.ordinalId;
    const data = (parserResult === null || parserResult === void 0 ? void 0 : parserResult.data) || (parserResult === null || parserResult === void 0 ? void 0 : parserResult.parsedData);
    // so far looks like parserResult is not needed for this function. we need to
    // double check. Also how it works for profile, bulk_profile parsers
    if (ordinalId != null && ordinalId >= 0 && (data === null || data === void 0 ? void 0 : data.contacts)) {
        const contactFromParser = (parserResult === null || parserResult === void 0 ? void 0 : parserResult.type) === 'bulk_profile' ? data.contacts[ordinalId] : data;
        return {
            parsedFullName: (contactFromParser === null || contactFromParser === void 0 ? void 0 : contactFromParser.fullName) || (contact === null || contact === void 0 ? void 0 : contact.fullName),
            parsedCompanyName: ((_b = (_a = contactFromParser === null || contactFromParser === void 0 ? void 0 : contactFromParser.currentPositions) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.companyName) || (contact === null || contact === void 0 ? void 0 : contact.companyName) || null,
            parsedLinkedinUrl: (contactFromParser === null || contactFromParser === void 0 ? void 0 : contactFromParser.socialLink) || (contact === null || contact === void 0 ? void 0 : contact.socialLink),
        };
    }
    // Page-level fields describe a single profile; on multi-contact pages (e.g. search results) they hold the page URL.
    const isSingleProfilePage = (parserResult === null || parserResult === void 0 ? void 0 : parserResult.type) === 'profile';
    const pageData = isSingleProfilePage ? data : undefined;
    return {
        parsedFullName: (pageData === null || pageData === void 0 ? void 0 : pageData.full_name) || (contact === null || contact === void 0 ? void 0 : contact.fullName),
        parsedCompanyName: ((_d = (_c = pageData === null || pageData === void 0 ? void 0 : pageData.organizations) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.name) || (pageData === null || pageData === void 0 ? void 0 : pageData.top_company) || (contact === null || contact === void 0 ? void 0 : contact.companyName) || null,
        parsedLinkedinUrl: (pageData === null || pageData === void 0 ? void 0 : pageData.socialLink) || (pageData === null || pageData === void 0 ? void 0 : pageData.link) || (contact === null || contact === void 0 ? void 0 : contact.socialLink),
    };
};
const mapContactTags = (tags) => {
    if (!Array.isArray(tags) || !tags.length) {
        return [];
    }
    return tags.map((tag) => ({
        id: String((tag === null || tag === void 0 ? void 0 : tag.id) || ''),
        name: String((tag === null || tag === void 0 ? void 0 : tag.name) || ''),
        color: String((tag === null || tag === void 0 ? void 0 : tag.color) || ''),
    }));
};
class Dispatcher {
    constructor() {
        this.actions = actions;
    }
    /**
     * Runs the All employees view's search with its current filters and title text. Writes only the
     * contacts map — never `state.company`. Returns the found contact keys.
     */
    searchEmployees({ search, filters: selectedFilters, searchTrigger, company_id, }) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            const state = getState();
            const payload = yield dispatch(CompaniesDispatcher.getSearchQuery({ id: company_id, searchTrigger }));
            const filters = mapEmployeesSearchFilters(selectedFilters, search);
            const result = yield searchEmployeesByCompany(Object.assign(Object.assign({}, payload), { filters, externalPluginVersion: state.settings.version }));
            dispatch(appActions.setIsSearchLimitReached(Boolean(result.searchQuotaExceeded)));
            const contacts = mapSearchEmployeesResultToContacts(result);
            dispatch(actions.setContacts(contacts));
            return contacts.map(item => item.contactId || item.id);
        });
    }
    setContact(contact) {
        return dispatch => {
            dispatch(actions.setContact(contact));
        };
    }
    setContacts(contacts) {
        return dispatch => {
            dispatch(actions.setContacts(contacts));
        };
    }
    updateContact(key, changes) {
        return dispatch => {
            if (!key) {
                return;
            }
            dispatch(actions.updateContact({ key, changes }));
        };
    }
    setCrmExistingIntegrations(contact, integrationNames) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            const crmExistingIntegrations = Array.isArray(integrationNames) ? integrationNames.map(name => String(name)) : [];
            if (areStringArraysEqual(existing.crmExistingIntegrations, crmExistingIntegrations)) {
                return;
            }
            dispatch(this.updateContact(key, { crmExistingIntegrations }));
        };
    }
    setSequenceNum(contact, sequenceNum) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key || existing.sequenceNum === sequenceNum) {
                return;
            }
            dispatch(this.updateContact(key, { sequenceNum }));
        };
    }
    incrementSequenceNum(contact) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing) {
                return;
            }
            dispatch(this.updateContact(key, { sequenceNum: (existing.sequenceNum || 0) + 1 }));
        };
    }
    setWishlistContact(contact, wishlistContact) {
        return (dispatch, getState) => {
            const currentKey = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[currentKey];
            if (!existing || !currentKey) {
                return;
            }
            const wishlistData = mapApiWishlistContactToWishlistData(wishlistContact);
            if (!wishlistData) {
                return;
            }
            const wishlistId = wishlistContact.id;
            const nextContactId = typeof wishlistId === 'string' || typeof wishlistId === 'number'
                ? String(wishlistId)
                : existing.contactId || currentKey;
            const tags = mapContactTags(wishlistContact.contactTags);
            dispatch(this.updateContact(currentKey, {
                contactId: nextContactId,
                wishlistData,
                tags,
            }));
            dispatch(actions.spawnContact(currentKey)); // id were changed, so spawn contact with next id
            dispatch(contactActions.setCurrentContactId(nextContactId)); // switch UI to new object
        };
    }
    resetFreshData(contact) {
        return (dispatch, getState) => {
            dispatch(this.updateContact(resolveExistingKey(getState().contacts, contact), {
                isFreshData: false,
                freshDataSummary: null,
            }));
        };
    }
    setFeedback(contact, dataPointKey, feedbackValue, payload) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            const contactId = typeof contact === 'string' ? contact : contact.contactId || '';
            yield submitContactFeedback(contactId, payload);
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            const dataPoint = existing === null || existing === void 0 ? void 0 : existing[dataPointKey];
            if (!existing || !dataPoint) {
                return;
            }
            dispatch(actions.updateContact({
                key,
                changes: {
                    [dataPointKey]: Object.assign(Object.assign({}, dataPoint), { feedback: feedbackValue }),
                },
            }));
        });
    }
    setIntegrationsData(contact, integrations) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            const incoming = integrations && typeof integrations === 'object' ? integrations : null;
            dispatch(actions.updateContact({
                key,
                changes: {
                    integrationsData: Object.assign(Object.assign({}, existing.integrationsData), mapIntegrationsData(incoming)),
                },
            }));
        };
    }
    setContactTable(contact, newList) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            const changes = { contactList: newList };
            if (selectIsWishlistContact(existing) && existing.wishlistData) {
                changes.wishlistData = Object.assign(Object.assign({}, existing.wishlistData), { lists: [...existing.wishlistData.lists, newList] });
            }
            dispatch(this.updateContact(key, changes));
        };
    }
    setContactIntegrationData(contact, integrationKey, data) {
        return (dispatch, getState) => {
            var _a, _b, _c, _d, _e, _f, _g;
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key || !integrationKey) {
                return;
            }
            const current = existing.integrationsData[integrationKey];
            const changes = {
                integrationsData: Object.assign(Object.assign({}, existing.integrationsData), { [integrationKey]: {
                        link: (_b = (_a = data.link) !== null && _a !== void 0 ? _a : current === null || current === void 0 ? void 0 : current.link) !== null && _b !== void 0 ? _b : '',
                        type: (_d = (_c = data.type) !== null && _c !== void 0 ? _c : current === null || current === void 0 ? void 0 : current.type) !== null && _d !== void 0 ? _d : null,
                        lastUpdated: (_e = data.lastUpdated) !== null && _e !== void 0 ? _e : null,
                        outdatedSince: (_f = data.outdatedSince) !== null && _f !== void 0 ? _f : null,
                    } }),
            };
            if (selectIsWishlistContact(existing) && existing.wishlistData) {
                changes.wishlistData = Object.assign(Object.assign({}, existing.wishlistData), { integrations: Object.assign(Object.assign({}, existing.wishlistData.integrations), { [integrationKey]: {
                            link: (_g = data.link) !== null && _g !== void 0 ? _g : null,
                        } }) });
            }
            dispatch(this.updateContact(key, changes));
        };
    }
    setSavedDatapoints(contact, integrations) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, {
                savedDatapoints: collectSavedDatapointIds(integrations, existing.savedDatapoints),
            }));
        };
    }
    onTagsChanged(contact, tags) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, { tags: mapContactTags(tags) }));
        };
    }
    setWishlistAddedData(contact, data) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key || !existing.wishlistData) {
                return;
            }
            const { emails, phones } = mapWishlistAddedDataPoints(data);
            dispatch(this.updateContact(key, {
                wishlistData: Object.assign(Object.assign({}, existing.wishlistData), { emails,
                    phones, email1: emails[0] || null, email2: emails[1] || null, phone1: phones[0] || null, phone2: phones[1] || null }),
            }));
        };
    }
    setContactEditableData(contact, data) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, mapContactEditableData(existing, data)));
        };
    }
    addContactData(contact, data, metadataParams) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            try {
                console.log('editContactData', data);
                const response = yield editContactData(Object.assign(Object.assign({}, data), { metadata: buildContactEditMetadata(getState(), metadataParams) }));
                messageList.searchInvalidate();
                const contactData = editedContactAdapter(response);
                dispatch(this.setContactEditableData(contact, contactData));
                const savedContactId = response === null || response === void 0 ? void 0 : response.id;
                if (savedContactId) {
                    dispatch(this.setContactEditableData(String(savedContactId), contactData));
                }
                notify({ type: 'success', message: 'Contact data added successfully' });
                return response;
            }
            catch (_a) {
                notify({ type: 'failure', message: 'Failed to add contact data' });
                return undefined;
            }
        });
    }
    addWishlistContactData(contact, data) {
        // updating contact email/phone
        return (dispatch) => __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield updateWishedContact(data);
                messageList.searchInvalidate();
                if (!response) {
                    notify({ type: 'failure', message: 'Failed to add contact data' });
                    return undefined;
                }
                dispatch(this.setWishlistAddedData(contact, response));
                notify({ type: 'success', message: 'Contact data added successfully' });
                return response;
            }
            catch (_a) {
                notify({ type: 'failure', message: 'Failed to add contact data' });
                return undefined;
            }
        });
    }
    editContact(contact, data, metadataParams) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            const isRevert = Boolean(metadataParams === null || metadataParams === void 0 ? void 0 : metadataParams.isRevert);
            try {
                const response = yield editContactData(Object.assign(Object.assign({}, data), { metadata: buildContactEditMetadata(getState(), metadataParams) }));
                const contactData = editedContactAdapter(response);
                dispatch(this.setContactEditableData(contact, contactData));
                const savedContactId = response === null || response === void 0 ? void 0 : response.id;
                if (savedContactId) {
                    dispatch(this.setContactEditableData(String(savedContactId), contactData));
                }
                notify({
                    type: 'success',
                    message: `Contact ${isRevert ? 'reverted' : 'edited'} successfully`,
                });
                return response;
            }
            catch (_a) {
                notify({ type: 'failure', message: 'Failed to edit contact' });
                return undefined;
            }
        });
    }
    editWishlistContact(contact, data) {
        return (dispatch) => __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield updateWishedContact(data);
                messageList.searchInvalidate();
                if (!response) {
                    notify({ type: 'failure', message: 'Failed to edit contact' });
                    return undefined;
                }
                dispatch(this.setWishlistAddedData(contact, response));
                notify({ type: 'success', message: 'Contact edited successfully' });
                return response;
            }
            catch (_a) {
                notify({ type: 'failure', message: 'Failed to edit contact' });
                return undefined;
            }
        });
    }
    editPartialProfileContact(contact, data, wishlistData, metadataParams) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            const isRevert = Boolean(metadataParams === null || metadataParams === void 0 ? void 0 : metadataParams.isRevert);
            try {
                yield editContactData(Object.assign(Object.assign({}, data), { metadata: buildContactEditMetadata(getState(), metadataParams) }));
                messageList.searchInvalidate();
                dispatch(this.setWishlistAddedData(contact, wishlistData));
                notify({
                    type: 'success',
                    message: `Contact ${isRevert ? 'reverted' : 'edited'} successfully`,
                });
                return true;
            }
            catch (_a) {
                notify({ type: 'failure', message: 'Failed to edit contact' });
                return false;
            }
        });
    }
    addPartialProfileContactData(contact, data, wishlistData, metadataParams) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            try {
                yield editContactData(Object.assign(Object.assign({}, data), { metadata: buildContactEditMetadata(getState(), metadataParams) }));
                messageList.searchInvalidate();
                dispatch(this.setWishlistAddedData(contact, wishlistData));
                notify({ type: 'success', message: 'Contact data added successfully' });
                return true;
            }
            catch (_a) {
                notify({ type: 'failure', message: 'Failed to add contact data' });
                return false;
            }
        });
    }
    refreshData(contact) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return undefined;
            }
            const contactId = existing.contactId || key;
            const requestId = existing.requestId ||
                (typeof contact === 'object' && contact && 'requestId' in contact ? contact.requestId : '') ||
                '';
            try {
                const response = yield refreshContactData({
                    requestId,
                    contacts: [contactId],
                });
                const contactResponse = response === null || response === void 0 ? void 0 : response[contactId];
                if (!(contactResponse === null || contactResponse === void 0 ? void 0 : contactResponse.isSuccess) || !contactResponse.contact) {
                    throw new Error(`Could not refresh contact id: ${contactId}`);
                }
                const mapped = mapSearchContactToContact(contactResponse.contact, {
                    requestId,
                    companyLid: existing.companyLid,
                });
                dispatch(this.setContact(Object.assign(Object.assign(Object.assign({}, existing), mapped), { id: existing.id || mapped.id, contactId: mapped.contactId || existing.contactId, requestId: mapped.requestId || existing.requestId, changes: ((_a = mapped.changes) === null || _a === void 0 ? void 0 : _a.length) ? mapped.changes : existing.changes || [] })));
                return contactResponse.contact;
            }
            catch (error) {
                throw error;
            }
        });
    }
    undoSave(contact) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, {
                contactList: '',
                integrationsData: {},
            }));
        };
    }
    setIsPressShow(contact, isPressShow) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, { isPressShow: Boolean(isPressShow) }));
        };
    }
    setIsOnboarding(contact, isOnboarding) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, { isOnboarding: Boolean(isOnboarding) }));
        };
    }
    setRevealedBy(contact, revealedBy) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, {
                revealedBy: typeof revealedBy === 'string' && revealedBy ? revealedBy : null,
            }));
        };
    }
    setShowDataPointType(contact, selectedDataPointType) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, {
                selectedDataPointType: selectedDataPointType == null ? null : String(selectedDataPointType),
            }));
        };
    }
    setShowCreditSplitPopup(contact, isOpen, creditSplitErrorType) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, {
                showCreditSplitPopup: Boolean(isOpen),
                creditSplitErrorType: typeof creditSplitErrorType === 'string' ? creditSplitErrorType : '',
            }));
        };
    }
    /**
     * Reveal API call. Returns show result + first contact response.
     */
    revealContact(showPayload) {
        return () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const numberOfCreditsCharged = sumNumberOfCreditsCharge((showPayload === null || showPayload === void 0 ? void 0 : showPayload.contacts) || []);
            const showResult = yield showContactsByDataPoints(showPayload);
            const response = (_d = (_c = (_b = (_a = showResult === null || showResult === void 0 ? void 0 : showResult.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.contacts) === null || _c === void 0 ? void 0 : _c.request) === null || _d === void 0 ? void 0 : _d[0];
            messageList.searchInvalidate();
            return { showResult, response, numberOfCreditsCharged };
        });
    }
    applyShowResponse(contact, showResponse, extras = {}) {
        return (dispatch, getState) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = key ? getState().contacts[key] : undefined;
            const mapped = mapEmployeeToContact(showResponse, {
                requestId: existing === null || existing === void 0 ? void 0 : existing.requestId,
                companyLid: existing === null || existing === void 0 ? void 0 : existing.companyLid,
            });
            const nextContact = Object.assign(Object.assign(Object.assign({}, (existing || mapped)), mapped), { id: (existing === null || existing === void 0 ? void 0 : existing.id) || mapped.id, requestId: (existing === null || existing === void 0 ? void 0 : existing.requestId) || mapped.requestId, ordinalId: (_a = existing === null || existing === void 0 ? void 0 : existing.ordinalId) !== null && _a !== void 0 ? _a : mapped.ordinalId, fullName: mapped.fullName || (existing === null || existing === void 0 ? void 0 : existing.fullName) || '', title: mapped.title || (existing === null || existing === void 0 ? void 0 : existing.title) || '', isFreshData: (_b = existing === null || existing === void 0 ? void 0 : existing.isFreshData) !== null && _b !== void 0 ? _b : mapped.isFreshData, freshDataSummary: (_c = existing === null || existing === void 0 ? void 0 : existing.freshDataSummary) !== null && _c !== void 0 ? _c : mapped.freshDataSummary, previousJob: ((_d = mapped.previousJob) === null || _d === void 0 ? void 0 : _d.changedJob)
                    ? mapped.previousJob
                    : ((_e = existing === null || existing === void 0 ? void 0 : existing.previousJob) !== null && _e !== void 0 ? _e : mapped.previousJob), uniqueSearchId: (existing === null || existing === void 0 ? void 0 : existing.uniqueSearchId) || mapped.uniqueSearchId, selectedDataPointType: (_f = existing === null || existing === void 0 ? void 0 : existing.selectedDataPointType) !== null && _f !== void 0 ? _f : mapped.selectedDataPointType, sequenceNum: (_h = (_g = existing === null || existing === void 0 ? void 0 : existing.sequenceNum) !== null && _g !== void 0 ? _g : mapped.sequenceNum) !== null && _h !== void 0 ? _h : 0, savedDatapoints: ((_j = existing === null || existing === void 0 ? void 0 : existing.savedDatapoints) === null || _j === void 0 ? void 0 : _j.length) ? existing.savedDatapoints : mapped.savedDatapoints, isPressShow: (_k = extras.isPressShow) !== null && _k !== void 0 ? _k : true, isRevealed: true });
            if (key && existing) {
                dispatch(this.updateContact(key, nextContact));
                return;
            }
            dispatch(this.setContact(nextContact));
        };
    }
    showContact(contact) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const state = getState();
            const key = resolveExistingKey(state.contacts, contact);
            const existing = key ? state.contacts[key] : undefined;
            if (!existing || !key) {
                return { errorCode: 500, message: 'Contact not found' };
            }
            if (existing.isOnboarding && existing.showDate) {
                dispatch(this.markContactRevealed(contact, true));
                return { data: { type: 'success' }, isOnboarding: true };
            }
            const selectedDataPointType = existing.isOnboarding
                ? BUTTON_TYPES.EMAIL_AND_PHONE
                : existing.selectedDataPointType || BUTTON_TYPES.EMAIL_AND_PHONE;
            const contactsPayload = buildContactsDataPointDataForShow([
                {
                    id: existing.id,
                    maskedContactId: existing.maskedContactId,
                    emails: selectEmails(existing),
                    phones: selectPhones(existing),
                },
            ], selectedDataPointType).filter(value => value !== null);
            const mappedPlatform = SAVE_CONTACT_SUB_PLATFORMS[state.settings.platform] || SUB_PLATFORM.LINKEDIN_MAIN;
            const showPayload = {
                request_id: existing.requestId,
                mask_id: existing.maskedId,
                is_onboarding: existing.isOnboarding,
                contacts: contactsPayload,
                metadata: {
                    source_product: mappedPlatform,
                    source_platform: PLATFORM.PLUGIN,
                    url: state.app.url,
                },
                use_bulk_credits: Boolean(sessionStorage.getItem(USE_BULK_KEY)),
            };
            try {
                const { showResult, response, numberOfCreditsCharged } = yield dispatch(this.revealContact(showPayload));
                if (response && ((_a = showResult === null || showResult === void 0 ? void 0 : showResult.data) === null || _a === void 0 ? void 0 : _a.type) === 'success') {
                    dispatch(this.applyShowResponse(contact, response, { isPressShow: true }));
                    dispatch(AccountDispatcher.fetchAccountCredits(numberOfCreditsCharged));
                }
                if (showResult) {
                    showResult.numberOfCreditsCharged = numberOfCreditsCharged;
                }
                return showResult;
            }
            catch (e) {
                return { errorCode: (_b = e.statusCode) !== null && _b !== void 0 ? _b : e.code, message: e.message, data: e === null || e === void 0 ? void 0 : e.data };
            }
        });
    }
    /** Mark contact revealed after successful show (and onboarding short-circuit). */
    markContactRevealed(contact, isPressShow = true) {
        return (dispatch, getState) => {
            const key = resolveExistingKey(getState().contacts, contact);
            const existing = getState().contacts[key];
            if (!existing || !key) {
                return;
            }
            dispatch(this.updateContact(key, {
                isRevealed: true,
                isPressShow: Boolean(isPressShow),
            }));
        };
    }
    saveToWishlistFromForm(contact, formData) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            const state = getState();
            const existing = state.contacts[resolveExistingKey(state.contacts, contact)];
            const { parsedFullName, parsedCompanyName, parsedLinkedinUrl } = getParsedContactData(state.app.parserResult, existing);
            const mappedPlatform = SAVE_CONTACT_SUB_PLATFORMS[state.settings.platform] || SUB_PLATFORM.LINKEDIN_MAIN;
            const payload = getContactWishlistData({
                contactCompany: formData.contactCompany,
                contactFirstName: formData.contactFirstName,
                contactLastName: formData.contactLastName,
                contactTitle: formData.contactTitle,
                contactEmail: formData.contactEmail,
                contactPhone: formData.contactPhone,
                parsedDataFullName: parsedFullName || [formData.contactFirstName, formData.contactLastName].filter(Boolean).join(' ').trim(),
                parsedDataCompany: parsedCompanyName,
                parsedLinkedinUrl,
                url: state.app.url,
                platform: mappedPlatform,
            });
            return dispatch(this.saveToWishlist(contact, payload));
        });
    }
    saveToWishlist(contact, wishlistPayload) {
        return (dispatch) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const response = yield saveContactToWishlist(wishlistPayload);
                messageList.searchInvalidate();
                const wishedContact = (_a = response === null || response === void 0 ? void 0 : response.wishedContacts) === null || _a === void 0 ? void 0 : _a[0];
                if (wishedContact) {
                    dispatch(this.setWishlistContact(contact, wishedContact));
                }
                return response;
            }
            catch (_b) {
                return null;
            }
        });
    }
}
const ContactsDispatcher = new Dispatcher();
export default ContactsDispatcher;
